import type { NavigateFunction } from "react-router-dom";

class NavigationService {
	private navigate: NavigateFunction | null = null;
	private readonly LAST_ROUTE_KEY = "lastVisitedRoute";

	setNavigate(navigateFunction: NavigateFunction) {
		this.navigate = navigateFunction;
	}

	navigateTo(path: string, options?: { replace?: boolean }) {
		if (this.navigate) {
			this.navigate(path, options);
			// Save the current path to localStorage for persistence
			this.saveLastRoute(path);
		} else {
			// Fallback to window.location if navigate is not available
			window.location.href = path;
			this.saveLastRoute(path);
		}
	}

	getCurrentPath(): string {
		return window.location.pathname;
	}

	/**
	 * Save the current route to localStorage
	 */
	saveLastRoute(path: string): void {
		try {
			// Only save valid routes (not login or root)
			if (path && path !== "/" && path !== "/login") {
				localStorage.setItem(this.LAST_ROUTE_KEY, path);
			} else {
				console.log("Skipped saving route (invalid):", path);
			}
		} catch (error) {
			console.warn("Failed to save last route:", error);
		}
	}

	/**
	 * Get the last visited route from localStorage
	 */
	getLastRoute(): string | null {
		try {
			return localStorage.getItem(this.LAST_ROUTE_KEY);
		} catch (error) {
			console.warn("Failed to get last route:", error);
			return null;
		}
	}

	/**
	 * Clear the saved route from localStorage
	 */
	clearLastRoute(): void {
		try {
			localStorage.removeItem(this.LAST_ROUTE_KEY);
		} catch (error) {
			console.warn("Failed to clear last route:", error);
		}
	}

	/**
	 * Navigate to the last saved route if available
	 */
	restoreLastRoute(): boolean {
		const lastRoute = this.getLastRoute();
		if (lastRoute && this.navigate) {
			this.navigate(lastRoute, { replace: true });
			return true;
		}
		return false;
	}

	/**
	 * Debug function to check navigation state
	 */
	debug(): void {
		console.log("Navigation Service Debug:", {
			currentPath: this.getCurrentPath(),
			lastRoute: this.getLastRoute(),
			hasNavigate: !!this.navigate,
			localStorage: {
				token: !!localStorage.getItem("token"),
				redirectPath: localStorage.getItem("redirectPath"),
				lastVisitedRoute: localStorage.getItem(this.LAST_ROUTE_KEY),
			},
		});
	}
}

export const navigationService = new NavigationService();
